import { Route, route as buildRoute } from "static-route-paths";

type RouteTree = Record<string, Route>;
type EmptyRouteTree = Record<never, never>;
type MixpanelRouteName = { mixpanelRouteName: string };
type MixpanelNamedRoute<TNested extends RouteTree = EmptyRouteTree> = Route & TNested & MixpanelRouteName;
type MixpanelRouteArgument = string | RouteTree | Route | undefined;
type MixpanelRouteResult<
  TPath extends MixpanelRouteArgument,
  TNested extends RouteTree | undefined,
> = TPath extends Route
  ? TPath & MixpanelRouteName
  : TNested extends RouteTree
  ? MixpanelNamedRoute<TNested>
  : TPath extends RouteTree
  ? MixpanelNamedRoute<TPath>
  : MixpanelNamedRoute;

export type MixpanelRouteDefinition = {
  mixpanelRouteName?: string;
  path: string;
};

const route: typeof buildRoute = buildRoute;

const isRoute = (value: unknown): value is Route => value instanceof Route;

/**
 * Builds a standard application route with a stable name for Mixpanel page tracking. Use this instead
 * of `route` when a route and its descendants should be grouped under an intentional analytics name
 * rather than reported by their URL pattern; use `route` when no Mixpanel-specific naming is needed.
 */
export const mixpanelRoute = <
  TPath extends MixpanelRouteArgument = undefined,
  TNested extends RouteTree | undefined = undefined,
>(
  mixpanelRouteName: string,
  path?: TPath,
  nestedRoutes?: TNested,
): MixpanelRouteResult<TPath, TNested> => {
  let routeEntry: Route | (Route & TNested);
  if (typeof path === "string") {
    const routePath: string = path;
    routeEntry = nestedRoutes === undefined ? route(routePath) : route(routePath, nestedRoutes);
  } else if (path === undefined) {
    routeEntry = route(mixpanelRouteName);
  } else if (isRoute(path)) {
    routeEntry = path;
  } else {
    routeEntry = route(path);
  }

  return Object.assign(routeEntry, { mixpanelRouteName }) as MixpanelRouteResult<TPath, TNested>;
};

const isMixpanelNamedRoute = (routeEntry: Route): routeEntry is MixpanelNamedRoute =>
  typeof (routeEntry as Partial<MixpanelNamedRoute>).mixpanelRouteName === "string";

export const collectMixpanelRouteDefinitions = (
  routeEntry: Route,
  inheritedMixpanelRouteName?: string,
): MixpanelRouteDefinition[] => {
  const mixpanelRouteName = isMixpanelNamedRoute(routeEntry)
    ? routeEntry.mixpanelRouteName
    : inheritedMixpanelRouteName;

  const routeDefinitions: MixpanelRouteDefinition[] = [{ mixpanelRouteName, path: routeEntry.path }];
  const subRoutes = routeEntry._subRoutes as RouteTree | undefined;

  Object.values(subRoutes ?? {}).forEach((subRoute) => {
    routeDefinitions.push(...collectMixpanelRouteDefinitions(subRoute, mixpanelRouteName));
  });

  return routeDefinitions;
};
